import { resolveSlotGenerationId } from './markGeneration';
import type { GalleryMediaItem } from '../interfaces/IWorkflowWidget';

export interface DraftFinalPayload {
  inputs: Record<string, unknown>;
  galleryUrls: Pick<GalleryMediaItem, 'url' | 'mediaType'>[];
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function readDraftJobId(outputs: unknown): string | null {
  const value = asRecord(outputs)?.draft_job_id;
  return typeof value === 'string' ? value.trim() || null : null;
}

/** Mirror the server extractor for stored gallery responses. */
export function extractDraftJobId(rawResponseJson: unknown): string | null {
  try {
    const response = asRecord(
      typeof rawResponseJson === 'string' ? JSON.parse(rawResponseJson) : rawResponseJson
    );
    const data = asRecord(response?.data);
    return readDraftJobId(data?.outputs) ?? readDraftJobId(response?.outputs);
  } catch {
    return null;
  }
}

/** Reuse persisted inputs, routing uploaded media through the dispatch media channel. */
export function buildDraftFinalPayload(rawInput: unknown, draftJobId: string): DraftFinalPayload {
  const source = asRecord(typeof rawInput === 'string' ? JSON.parse(rawInput) : rawInput);
  if (!source) throw new Error('Invalid draft raw_input');
  if (!draftJobId.trim()) throw new Error('Missing draft job ID');

  const inputs: Record<string, unknown> = {
    ...source,
    draft_job_id: draftJobId.trim(),
    resolution: '1080p',
  };
  delete inputs.draft;

  const galleryUrls: DraftFinalPayload['galleryUrls'] = [];
  for (const [key, mediaType] of [
    ['image_urls', 'image'],
    ['video_urls', 'video'],
    ['audio_urls', 'audio'],
  ] as const) {
    const value = inputs[key];
    delete inputs[key];
    if (value === undefined || value === null) continue;
    const urls: unknown = typeof value === 'string' ? JSON.parse(value) : value;
    // Reject malformed persisted media rather than silently launching without references.
    if (!Array.isArray(urls) || !urls.every(url => typeof url === 'string' && url.trim())) {
      throw new Error(`Invalid draft ${key}`);
    }
    for (const url of urls) galleryUrls.push({ url, mediaType });
  }

  return { inputs, galleryUrls };
}

/**
 * Whether a workbench slot is a finished draft that can be turned into a final.
 * The one gate for every surface that shows the action (slot, list row, preview modal).
 */
export function isDraftFinalCandidate(slot: {
  id: string;
  asyncGenerationId?: number;
  status: string;
  draftJobId?: string;
}): boolean {
  return slot.status === 'completed'
    && !!slot.draftJobId?.trim()
    && resolveSlotGenerationId(slot.id, slot.asyncGenerationId) !== null;
}

/** URL parameter that hands a gallery draft over to the workbench confirmation. */
export const DRAFT_FINAL_HANDOFF_PARAM = 'finalizeDraft';

/** Generation section that hosts a draft of this content type. */
export function draftFinalSectionRoute(contentType?: string | null): string {
  const type = (contentType || '').toLowerCase();
  if (type.startsWith('video')) return '/video';
  if (type.startsWith('audio')) return '/audio';
  if (type === '3d' || type.startsWith('model/')) return '/3d';
  if (type === 'text') return '/text';
  return '/image';
}

/**
 * Workbench path that opens the draft-final confirmation for a gallery item.
 * Carries only identifiers: the workbench reloads the draft from the server,
 * and nothing is debited until the user confirms there.
 */
export function buildDraftFinalHandoffPath(
  contentType: string | null | undefined,
  workflowId: number,
  generationId: number,
): string {
  const params = new URLSearchParams({
    workflow: String(workflowId),
    [DRAFT_FINAL_HANDOFF_PARAM]: String(generationId),
  });
  return `${draftFinalSectionRoute(contentType)}?${params.toString()}`;
}

/** Positive integer generation ID from the hand-off parameter, else null. */
export function parseDraftFinalHandoffId(value: string | null | undefined): number | null {
  if (!value || !/^\d+$/.test(value.trim())) return null;
  const id = Number(value.trim());
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
